import {
	NavigationTree,
	NavTreeItem,
} from '../../logic/domains/navigationTree.domain';
import { isInNavigationTree } from '../../logic/functions/common/navigationTreeCheck';
import _ from 'lodash';
import { Breadcrumb } from '../../components/presentational/HeaderBanner/Breadcrumbs/Breadcrumbs';

export interface BannerItem {
	key: string;
	path: string;
	title: string;
	breadcrumbs: Breadcrumb[];
	titleTestId?: string;
}

export const getAllBannerItems = (
	navigationTree: NavigationTree,
	configuredRoutes: NavTreeItem[]
): BannerItem[] => {
	return _(navigationTree.menuData)
		.map(menuItem => mapNavItemToBannerItems(menuItem, configuredRoutes, false))
		.flatten()
		.concat(
			_(configuredRoutes)
				.map(configuredRoute =>
					getNonNavTreeBannerItems(
						navigationTree.menuData,
						configuredRoute,
						false
					)
				)
				.flatten()
				.value()
		)
		.value();
};

const mapNavItemToBannerItems = (
	navigationItem: NavTreeItem,
	configuredRoutes: NavTreeItem[],
	isSubpage: boolean,
	breadcrumbs?: Breadcrumb[]
): BannerItem[] => {
	let bannerNavItems: BannerItem[] = new Array();
	const mappedBreadcrumbs = getNavItemBreadcrumbs(navigationItem, breadcrumbs);
	const breadcrumbsForSubpage = _.cloneDeep(mappedBreadcrumbs);
	const mappedNavItem = getConfiguredNavItem(
		navigationItem,
		mappedBreadcrumbs,
		configuredRoutes
	);
	if (mappedNavItem) {
		if (!isSubpage) {
			mappedNavItem.breadcrumbs.pop();
		}
		bannerNavItems.push(mappedNavItem);
	}
	bannerNavItems = bannerNavItems.concat(
		getSubpageMappings(navigationItem, configuredRoutes, breadcrumbsForSubpage)
	);
	return bannerNavItems;
};

const getSubpageMappings = (
	navigationItem: NavTreeItem,
	configuredRoutes: NavTreeItem[],
	breadcrumbs: Breadcrumb[]
) => {
	let subpageBannerItems: BannerItem[] = new Array();
	if (navigationItem.subpages && navigationItem.subpages.length > 0) {
		navigationItem.subpages.forEach(subpage => {
			subpageBannerItems = subpageBannerItems.concat(
				mapNavItemToBannerItems(subpage, configuredRoutes, true, breadcrumbs)
			);
			breadcrumbs.pop();
		});
	}
	return subpageBannerItems;
};

const getConfiguredNavItem = (
	navigationItem: NavTreeItem,
	breadcrumbs: Breadcrumb[],
	configuredRoutes: NavTreeItem[]
): BannerItem | undefined => {
	const matchedNavItem = _.find(
		configuredRoutes,
		(configuredRoute: NavTreeItem) => {
			if (configuredRoute.showBanner) {
				return configuredRoute.key === navigationItem.key;
			}
			return false;
		}
	);

	if (matchedNavItem) {
		const newBannerItem = {
			key: matchedNavItem.key,
			path: matchedNavItem.path,
			title: navigationItem.title,
			breadcrumbs: breadcrumbs,
			titleTestId: navigationItem.titleTestId,
		};
		return newBannerItem;
	}
	return;
};

const getNavItemBreadcrumbs = (
	navigationItem: NavTreeItem,
	breadcrumbs?: Breadcrumb[]
): Breadcrumb[] => {
	const navItemBreadcrumb: Breadcrumb = {
		title: navigationItem.title,
		relativePath: navigationItem.path,
		absolutePath: navigationItem.url,
	};
	if (!breadcrumbs) {
		breadcrumbs = new Array<Breadcrumb>();
		breadcrumbs.push({ title: 'Home', relativePath: '/' });
	}
	breadcrumbs.push(navItemBreadcrumb);
	return breadcrumbs;
};

// Returns banner items for routes that are configured in the SPA but not returned by nabfeed
const getNonNavTreeBannerItems = (
	navigationTreeData: NavTreeItem[],
	configuredRoute: NavTreeItem,
	isSubpage: boolean,
	breadcrumbs?: Breadcrumb[]
): BannerItem[] => {
	let bannerNavItems: BannerItem[] = new Array();
	const mappedBreadcrumbs = getNavItemBreadcrumbs(configuredRoute, breadcrumbs);
	const breadcrumbsForSubpage = _.cloneDeep(mappedBreadcrumbs);
	if (
		!isInNavigationTree(navigationTreeData, configuredRoute) &&
		configuredRoute.showBanner
	) {
		const newBannerItem = {
			key: configuredRoute.key,
			path: configuredRoute.path,
			title: configuredRoute.title,
			breadcrumbs: mappedBreadcrumbs,
			titleTestId: configuredRoute.titleTestId,
		};
		if (!isSubpage) {
			newBannerItem.breadcrumbs.pop();
		}
		bannerNavItems.push(newBannerItem);
	}
	bannerNavItems = bannerNavItems.concat(
		getNonNavSubpages(
			navigationTreeData,
			configuredRoute,
			breadcrumbsForSubpage
		)
	);

	return bannerNavItems;
};

const getNonNavSubpages = (
	navigationTreeData: NavTreeItem[],
	configuredRoute: NavTreeItem,
	breadcrumbs: Breadcrumb[]
): BannerItem[] => {
	let subpageBannerItems: BannerItem[] = new Array();
	if (configuredRoute.subpages && configuredRoute.subpages.length > 0) {
		configuredRoute.subpages.forEach(subpage => {
			subpageBannerItems = subpageBannerItems.concat(
				getNonNavTreeBannerItems(navigationTreeData, subpage, true, breadcrumbs)
			);
			breadcrumbs.pop();
		});
	}
	return subpageBannerItems;
};

export default getAllBannerItems;
